import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../../../components/ui/command";

/**
 * Generic searchable combobox (client-side filtered) used everywhere a
 * master-data list can grow past a handful of items - Ledgers, Vendors,
 * Bank Accounts, Funds, Fixed Deposits, and Journal Voucher grid rows.
 * Replaces the long plain <Select> dropdowns the earlier Phase 5 pass used
 * for these, per the "Searchable Selectors" correction: a user typing "sec"
 * should immediately see "Security salary expense" / "Security vendor
 * payable" instead of scrolling the full Chart of Accounts.
 *
 * `options`: [{ value, label, sublabel, group, keywords: [] }]. Typing
 * filters on label + sublabel + keywords (e.g. a vendor's mobile/GSTIN, a
 * ledger's group name), not just the visible label. Results are grouped by
 * `group` when more than one distinct group is present (ungrouped options
 * fall under a single unlabeled group, which renders with no heading).
 *
 * This is deliberately a CLIENT-SIDE filter over an already-fetched option
 * list - appropriate for Ledgers/Vendors/Bank Accounts/Funds, which are
 * bounded master-data lists (a Society realistically has dozens, not
 * thousands). For Member search, which can genuinely be large, the existing
 * PartySearchInput's debounced server-side `/society/directory?q=` search is
 * used instead (see PartySearchInput.jsx) rather than forcing every list
 * through one client-filtered component.
 */
export default function SearchableSelect({
  value, onChange, options, placeholder = "Select...", searchPlaceholder = "Search...",
  emptyText = "No matches.", disabled, clearable = false, loading = false, className,
}) {
  const [open, setOpen] = useState(false);
  const safeOptions = options || [];
  const selected = useMemo(() => safeOptions.find((o) => o.value === value), [safeOptions, value]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const opt of safeOptions) {
      const g = opt.group || "";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(opt);
    }
    return Array.from(map.entries());
  }, [safeOptions]);

  const filterFn = (itemValue, search) => {
    if (itemValue === "__clear__") return 1;
    const opt = safeOptions.find((o) => o.value === itemValue);
    if (!opt) return 0;
    const haystack = [opt.label, opt.sublabel, ...(opt.keywords || [])].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(search.toLowerCase()) ? 1 : 0;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn("w-full justify-between font-normal", !selected && "text-[#6B7280]", className)}
        >
          <span className="truncate">{loading ? "Loading..." : selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={filterFn}>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {clearable && value && (
              <CommandGroup>
                <CommandItem value="__clear__" onSelect={() => { onChange(""); setOpen(false); }}>
                  <X className="mr-2 h-4 w-4" /> Clear selection
                </CommandItem>
              </CommandGroup>
            )}
            {groups.map(([group, items]) => (
              <CommandGroup key={group || "_ungrouped"} heading={group || undefined}>
                {items.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                    onSelect={() => { onChange(opt.value); setOpen(false); }}
                  >
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === opt.value ? "opacity-100" : "opacity-0")} />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{opt.label}</span>
                      {opt.sublabel && <span className="truncate text-xs text-[#6B7280]">{opt.sublabel}</span>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
